import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Camera, RotateCw, Ruler, RefreshCcw, Sparkles, AlertCircle, Info, Crosshair,
} from 'lucide-react';
import { Slider } from '../ui/slider';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { HudCard, HudButton } from '../hud';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HUMAN_EYE_HEIGHT_M = 1.65;  // standard FPS eye-level (shared with backend)

/**
 * FpsCameraPlanner
 * ----------------
 * Interactive top-down camera placement for FPS (eye-level interior) renders.
 *
 *  • Loads the lot's selected plan file (selected_plan_file_id) and renders it
 *    as a background image inside a fixed-aspect viewport.
 *  • Overlays a draggable camera marker + rotation handle + semi-transparent
 *    FOV cone (SVG sector).
 *  • FOV is user-controlled via a slider (50°–110°, default 75° / 28mm).
 *  • Optional 2-click calibration converts pixel coordinates → meters.
 *  • Exposes a single callback `onConfirm({ x_rel, y_rel, angle_deg, fov_deg,
 *    scale_m_per_px?, plan_file_id, lot_id })` for the parent to trigger the
 *    backend FPS generation. The marker geometry is stored in *relative* coords
 *    (0–1) so it stays anchored even if the plan is rescaled.
 *
 * Props
 *  • projectId      : string (required)  — used to fetch lots and trigger gen
 *  • initialLotId   : string (optional)  — pre-select a lot
 *  • onConfirm      : ({…payload}) => void (optional)
 *  • onClose        : () => void (optional)  — back button
 */
const FpsCameraPlanner = ({ projectId, initialLotId = null, onConfirm, onClose }) => {
  // ===== Lots & plan source =====
  const [lots, setLots] = useState([]);
  const [lotId, setLotId] = useState(initialLotId);
  const [loadingLots, setLoadingLots] = useState(true);

  // ===== Camera placement (relative coordinates 0-1) =====
  const [cameraX, setCameraX] = useState(0.5);    // x position (0-1)
  const [cameraY, setCameraY] = useState(0.5);    // y position (0-1)
  const [angleDeg, setAngleDeg] = useState(0);    // 0° points right (east), grows clockwise
  const [fovDeg, setFovDeg] = useState(75);       // 50-110, default 75 (28mm equiv.)

  // ===== Calibration (optional, 2 clicks → distance in meters → m/pixel) =====
  const [calibMode, setCalibMode] = useState('idle');   // idle | pick1 | pick2 | done
  const [calibA, setCalibA] = useState(null);           // {x_rel, y_rel}
  const [calibB, setCalibB] = useState(null);
  const [calibMeters, setCalibMeters] = useState('');   // input string
  const [scaleMPerPx, setScaleMPerPx] = useState(null); // computed scale

  // ===== Drag state (refs to avoid re-renders mid-drag) =====
  const dragRef = useRef(null);          // 'camera' | 'handle' | 'pan' | null
  const viewportRef = useRef(null);
  const planImgRef = useRef(null);
  const [planDims, setPlanDims] = useState({ w: 0, h: 0 });  // natural plan size in CSS pixels (post-fit)

  // ===== Zoom & pan (relative — never breaks the 0-1 camera coordinates) =====
  // zoom = 1.0 → plan fitted to viewport; we let the user go up to 5× for
  // fine FPS placement & calibration on detailed floors. Pan is stored in
  // viewport CSS pixels (will be normalized when computing rel coordinates).
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panStartRef = useRef(null);    // {clientX, clientY, panX, panY}
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 6;

  // Wheel zoom anchored on the cursor (so the point under the mouse stays
  // visually under the mouse — same UX as Figma / Maps).
  const onViewportWheel = (e) => {
    const vp = viewportRef.current;
    if (!vp) return;
    e.preventDefault();
    const rect = vp.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((prev) => {
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, prev * factor));
      // Anchor the cursor: solve for new pan so (cx,cy) stays on the same
      // image point. p_img = (cursor - pan) / prev  ⇒ new_pan = cursor - p_img * next
      setPan((curPan) => {
        const pImgX = (cx - curPan.x) / prev;
        const pImgY = (cy - curPan.y) / prev;
        return { x: cx - pImgX * next, y: cy - pImgY * next };
      });
      return next;
    });
  };

  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const stepZoom = (delta) => {
    const vp = viewportRef.current;
    if (!vp) { setZoom((z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z + delta))); return; }
    // Anchor at viewport center on button click.
    const rect = vp.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setZoom((prev) => {
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, prev + delta));
      setPan((curPan) => {
        const pImgX = (cx - curPan.x) / prev;
        const pImgY = (cy - curPan.y) / prev;
        return { x: cx - pImgX * next, y: cy - pImgY * next };
      });
      return next;
    });
  };

  // ===== Load lots =====
  useEffect(() => {
    if (!projectId) return;
    setLoadingLots(true);
    axios.get(`${API}/projects/${projectId}/lots`, { withCredentials: true })
      .then(r => {
        const eligible = (r.data || []).filter(l => l.selected_plan_file_id);
        setLots(eligible);
        if (!lotId && eligible.length > 0) setLotId(eligible[0].lot_id);
      })
      .catch(() => toast.error('Impossible de charger les lots'))
      .finally(() => setLoadingLots(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const selectedLot = useMemo(() => lots.find(l => l.lot_id === lotId), [lots, lotId]);
  const planUrl = selectedLot?.selected_plan_file_id
    ? `${API}/files/${selectedLot.selected_plan_file_id}/download`
    : null;

  // ===== When plan image loads → capture natural CSS pixel dimensions =====
  const handlePlanLoaded = (e) => {
    const img = e.currentTarget;
    setPlanDims({ w: img.clientWidth, h: img.clientHeight });
  };

  // Re-measure on window resize (the plan is responsive)
  useEffect(() => {
    const onResize = () => {
      if (planImgRef.current) {
        setPlanDims({ w: planImgRef.current.clientWidth, h: planImgRef.current.clientHeight });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ===== Drag handlers (pointer events — works for mouse + touch + pen) =====
  // Relative coordinates must remain anchored to the IMAGE space (not the
  // viewport), otherwise the camera marker would jump every time the user
  // zooms or pans. We invert the (zoom, pan) transform here.
  const getRelFromEvent = (e) => {
    const vp = viewportRef.current;
    if (!vp) return null;
    const r = vp.getBoundingClientRect();
    const cx = e.clientX - r.left;
    const cy = e.clientY - r.top;
    // Inverse transform: image-space pixel = (viewport-space pixel - pan) / zoom
    const ix = (cx - pan.x) / zoom;
    const iy = (cy - pan.y) / zoom;
    return {
      x_rel: Math.max(0, Math.min(1, ix / r.width)),
      y_rel: Math.max(0, Math.min(1, iy / r.height)),
    };
  };

  // Pan via middle-mouse OR Space-drag OR right-button drag on empty area.
  const onViewportPointerDown = (e) => {
    // If the click landed on a draggable handle/camera, let those handlers run.
    if (dragRef.current) return;
    // Pan triggers: middle button OR space-held (we use right click — middle isn't always present on trackpads).
    const isPanTrigger = e.button === 1 || e.button === 2 || e.shiftKey;
    if (isPanTrigger) {
      e.preventDefault();
      dragRef.current = 'pan';
      panStartRef.current = { clientX: e.clientX, clientY: e.clientY, panX: pan.x, panY: pan.y };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
  };

  const onPointerDownCamera = (e) => {
    e.stopPropagation();
    dragRef.current = 'camera';
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerDownHandle = (e) => {
    e.stopPropagation();
    dragRef.current = 'handle';
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    if (dragRef.current === 'pan') {
      const start = panStartRef.current;
      if (!start) return;
      setPan({ x: start.panX + (e.clientX - start.clientX), y: start.panY + (e.clientY - start.clientY) });
      return;
    }
    const rel = getRelFromEvent(e);
    if (!rel) return;
    if (dragRef.current === 'camera') {
      setCameraX(rel.x_rel);
      setCameraY(rel.y_rel);
    } else if (dragRef.current === 'handle') {
      // Compute angle from camera to pointer (degrees, 0 = +x axis, CW positive)
      const dx = rel.x_rel - cameraX;
      const dy = rel.y_rel - cameraY;
      if (Math.abs(dx) + Math.abs(dy) > 0.001) {
        let a = (Math.atan2(dy, dx) * 180) / Math.PI;
        if (a < 0) a += 360;
        setAngleDeg(Math.round(a));
      }
    }
  };
  const onPointerUp = () => {
    dragRef.current = null;
    panStartRef.current = null;
  };

  // ===== Viewport click for calibration =====
  const onViewportClick = (e) => {
    if (calibMode === 'idle' || calibMode === 'done') return;
    if (dragRef.current) return;  // ignore clicks during a drag
    const rel = getRelFromEvent(e);
    if (!rel) return;
    if (calibMode === 'pick1') {
      setCalibA(rel);
      setCalibMode('pick2');
      toast('Point A enregistré. Clique le point B (distance connue).', { icon: <Crosshair className="w-4 h-4" /> });
    } else if (calibMode === 'pick2') {
      setCalibB(rel);
      setCalibMode('done');
      toast('Point B enregistré. Entre la distance réelle en mètres.', { icon: <Ruler className="w-4 h-4" /> });
    }
  };

  const computeCalibration = () => {
    const meters = parseFloat(calibMeters);
    if (!calibA || !calibB || !meters || meters <= 0) {
      toast.error('Renseigne 2 points et une distance > 0');
      return;
    }
    const dxRel = calibB.x_rel - calibA.x_rel;
    const dyRel = calibB.y_rel - calibA.y_rel;
    // Convert to CSS pixels (relative * planDims), then meters per pixel
    const pxDist = Math.sqrt((dxRel * planDims.w) ** 2 + (dyRel * planDims.h) ** 2);
    if (pxDist < 4) { toast.error('Points trop rapprochés — augmente la distance.'); return; }
    setScaleMPerPx(meters / pxDist);
    toast.success(`Calibration OK : 1 px = ${(meters / pxDist).toFixed(4)} m`);
  };

  const resetCalibration = () => {
    setCalibMode('idle'); setCalibA(null); setCalibB(null);
    setCalibMeters(''); setScaleMPerPx(null);
  };

  // ===== Derived geometry for SVG overlay (in CSS pixels) =====
  const overlay = useMemo(() => {
    if (!planDims.w || !planDims.h) return null;
    const cx = cameraX * planDims.w;
    const cy = cameraY * planDims.h;
    const r = Math.min(planDims.w, planDims.h) * 0.18;  // cone length ~18% of plan side
    const halfFov = (fovDeg / 2) * (Math.PI / 180);
    const dir = (angleDeg * Math.PI) / 180;
    const x1 = cx + r * Math.cos(dir - halfFov);
    const y1 = cy + r * Math.sin(dir - halfFov);
    const x2 = cx + r * Math.cos(dir + halfFov);
    const y2 = cy + r * Math.sin(dir + halfFov);
    const largeArc = fovDeg > 180 ? 1 : 0;
    const conePath = `M ${cx},${cy} L ${x1},${y1} A ${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
    // Handle position (60% along cone direction)
    const hr = r * 0.62;
    const hx = cx + hr * Math.cos(dir);
    const hy = cy + hr * Math.sin(dir);
    return { cx, cy, r, conePath, hx, hy };
  }, [cameraX, cameraY, angleDeg, fovDeg, planDims]);

  // ===== Confirm: build payload =====
  const handleConfirm = () => {
    if (!selectedLot) { toast.error('Choisis un lot avec un plan'); return; }
    const payload = {
      view_type: 'fps',
      project_id: projectId,
      lot_id: selectedLot.lot_id,
      plan_file_id: selectedLot.selected_plan_file_id,
      camera_position: {
        x: Number(cameraX.toFixed(4)),  // relative 0-1
        y: Number(cameraY.toFixed(4)),
        z: HUMAN_EYE_HEIGHT_M,           // human eye level
        angle: angleDeg,                 // 0-360, CW from east
      },
      fov_deg: fovDeg,
      scale_m_per_px: scaleMPerPx,       // null if not calibrated
    };
    onConfirm?.(payload);
  };

  const reset = () => {
    setCameraX(0.5); setCameraY(0.5); setAngleDeg(0); setFovDeg(75);
    resetCalibration();
  };

  // ===== Render =====
  return (
    <div className="space-y-4" data-testid="fps-camera-planner">
      {/* Header */}
      <HudCard variant="elevated">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Studio FPS</span>
            <h2 className="mt-0.5 text-lg font-semibold tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)' }}>
              Placement caméra · hauteur d'homme
            </h2>
            <p className="mt-1 text-[12px]" style={{ color: 'var(--c-text-muted)' }}>
              Glisse la caméra sur le plan, oriente la poignée, ajuste le FOV.
              Z fixé à {HUMAN_EYE_HEIGHT_M}m (yeux humains).
            </p>
          </div>
          <div className="flex items-center gap-2">
            {onClose && (
              <HudButton variant="ghost" size="sm" onClick={onClose} data-testid="fps-close-btn">
                Fermer
              </HudButton>
            )}
            <HudButton variant="ghost" size="sm" leftIcon={<RefreshCcw className="w-3 h-3" />} onClick={reset} data-testid="fps-reset-btn">
              Réinitialiser
            </HudButton>
          </div>
        </div>
      </HudCard>

      {/* Lot selector */}
      <HudCard variant="glass">
        <div className="flex flex-wrap items-center gap-3">
          <span className="hud-eyebrow">Lot ·  Plan source</span>
          {loadingLots ? (
            <span className="text-[12px]" style={{ color: 'var(--c-text-muted)' }}>Chargement…</span>
          ) : lots.length === 0 ? (
            <div className="flex items-center gap-2 text-[12px]" style={{ color: '#FCA5A5' }}>
              <AlertCircle className="w-3.5 h-3.5" />
              Aucun lot avec plan retenu. Sélectionne d'abord un plan dans Ingénierie IA.
            </div>
          ) : (
            <div className="min-w-[260px]">
              <Select value={lotId || ''} onValueChange={setLotId}>
                <SelectTrigger className="h-9" data-testid="fps-lot-select">
                  <SelectValue placeholder="Choisir un lot" />
                </SelectTrigger>
                <SelectContent>
                  {lots.map(l => (
                    <SelectItem key={l.lot_id} value={l.lot_id}>
                      {l.lot_number || l.name} · {l.typology || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </HudCard>

      {/* Main interactive area */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Plan viewport */}
        <HudCard variant="glass" className="overflow-hidden p-0 relative">
          {/* Zoom toolbar (top-right of the canvas) */}
          {planUrl && (
            <div
              className="absolute top-2 right-2 z-20 flex flex-col gap-1 rounded-md"
              style={{ background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)' }}
              data-testid="fps-zoom-toolbar"
            >
              <button
                type="button"
                aria-label="Zoom avant"
                onClick={() => stepZoom(0.4)}
                className="w-7 h-7 grid place-items-center text-white text-lg leading-none hover:bg-white/10 transition-colors"
                data-testid="fps-zoom-in"
              >
                +
              </button>
              <button
                type="button"
                aria-label="Zoom arrière"
                onClick={() => stepZoom(-0.4)}
                className="w-7 h-7 grid place-items-center text-white text-lg leading-none hover:bg-white/10 transition-colors"
                data-testid="fps-zoom-out"
              >
                −
              </button>
              <button
                type="button"
                aria-label="Réinitialiser la vue"
                onClick={resetView}
                className="w-7 h-7 grid place-items-center text-white hover:bg-white/10 transition-colors"
                title="Réinitialiser (1:1)"
                data-testid="fps-zoom-reset"
              >
                <RefreshCcw className="w-3.5 h-3.5" />
              </button>
              <div
                className="w-7 text-center hud-tabular text-[10px] py-1 border-t border-white/10"
                style={{ color: 'var(--c-gold)' }}
                data-testid="fps-zoom-level"
              >
                {Math.round(zoom * 100)}%
              </div>
            </div>
          )}
          <div
            ref={viewportRef}
            className="relative w-full select-none"
            style={{
              cursor: calibMode === 'pick1' || calibMode === 'pick2' ? 'crosshair' : (dragRef.current === 'pan' ? 'grabbing' : 'default'),
              background: 'rgba(255,255,255,0.02)',
              minHeight: 360,
              aspectRatio: planDims.w && planDims.h ? `${planDims.w} / ${planDims.h}` : '4 / 3',
              overflow: 'hidden',
            }}
            onWheel={onViewportWheel}
            onPointerDown={onViewportPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onContextMenu={(e) => e.preventDefault()}
            onClick={onViewportClick}
            data-testid="fps-plan-viewport"
          >
            {/* Zoom/pan wrapper — both the plan image and the SVG overlay share the
                same transform so the camera marker stays anchored to the plan. */}
            <div
              className="absolute inset-0"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
                pointerEvents: 'none',
              }}
            >
              {planUrl && (
                <img
                  ref={planImgRef}
                  src={planUrl}
                  alt="Plan top-down"
                  onLoad={handlePlanLoaded}
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                  draggable={false}
                />
              )}
            </div>
            {!planUrl && (
              <div className="absolute inset-0 flex items-center justify-center text-center px-6">
                <p className="text-[13px]" style={{ color: 'var(--c-text-muted)' }}>
                  Choisis un lot avec un plan retenu pour démarrer.
                </p>
              </div>
            )}

            {/* SVG overlay — FOV cone, camera, rotation handle, calibration markers.
                Wrapped in the SAME zoom/pan transform as the plan image so the camera
                marker stays glued to the underlying geometry. `pointerEvents:auto`
                only on the interactive handles ensures pan still works on empty area. */}
            {planUrl && overlay && (
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox={`0 0 ${planDims.w} ${planDims.h}`}
                preserveAspectRatio="none"
                style={{
                  touchAction: 'none',
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: '0 0',
                  pointerEvents: 'none',
                }}
              >
                {/* FOV cone */}
                <path
                  d={overlay.conePath}
                  fill="rgba(217,160,91,0.18)"
                  stroke="rgba(217,160,91,0.7)"
                  strokeWidth="1.2"
                  pointerEvents="none"
                  data-testid="fps-cone"
                />

                {/* Camera-to-handle line (visual cue) */}
                <line
                  x1={overlay.cx} y1={overlay.cy}
                  x2={overlay.hx} y2={overlay.hy}
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth="1.2"
                  strokeDasharray="4,3"
                  pointerEvents="none"
                />

                {/* Calibration points */}
                {calibA && (
                  <circle cx={calibA.x_rel * planDims.w} cy={calibA.y_rel * planDims.h}
                          r={6} fill="#5EEAD4" stroke="#0E0E10" strokeWidth="2" pointerEvents="none" />
                )}
                {calibB && (
                  <circle cx={calibB.x_rel * planDims.w} cy={calibB.y_rel * planDims.h}
                          r={6} fill="#5EEAD4" stroke="#0E0E10" strokeWidth="2" pointerEvents="none" />
                )}
                {calibA && calibB && (
                  <line x1={calibA.x_rel * planDims.w} y1={calibA.y_rel * planDims.h}
                        x2={calibB.x_rel * planDims.w} y2={calibB.y_rel * planDims.h}
                        stroke="#5EEAD4" strokeWidth="1.5" strokeDasharray="6,4" pointerEvents="none" />
                )}

                {/* Rotation handle */}
                <circle
                  cx={overlay.hx} cy={overlay.hy}
                  r={9}
                  fill="#FFFFFF"
                  stroke="#D9A05B"
                  strokeWidth="2"
                  style={{ cursor: 'grab', pointerEvents: 'auto' }}
                  onPointerDown={onPointerDownHandle}
                  data-testid="fps-handle"
                />

                {/* Camera marker (gold circle with lens dot) */}
                <g
                  style={{ cursor: 'grab', pointerEvents: 'auto' }}
                  onPointerDown={onPointerDownCamera}
                  data-testid="fps-camera-marker"
                >
                  <circle cx={overlay.cx} cy={overlay.cy} r={14}
                          fill="#D9A05B" stroke="#0E0E10" strokeWidth="2.5" />
                  <circle cx={overlay.cx} cy={overlay.cy} r={5}
                          fill="#0E0E10" pointerEvents="none" />
                </g>
              </svg>
            )}
          </div>
        </HudCard>

        {/* Controls */}
        <div className="space-y-3">
          {/* FOV */}
          <HudCard variant="glass">
            <div className="flex items-center justify-between mb-2">
              <span className="hud-eyebrow">Champ de vision</span>
              <span className="hud-tabular text-[13px] font-semibold" style={{ color: 'var(--c-gold)' }}>
                {fovDeg}°
              </span>
            </div>
            <Slider
              min={50} max={110} step={1}
              value={[fovDeg]}
              onValueChange={(v) => setFovDeg(v[0])}
              data-testid="fps-fov-slider"
            />
            <div className="flex justify-between mt-1 text-[10px]" style={{ color: 'var(--c-text-faint)' }}>
              <span>50° · télé</span>
              <span>75° · standard</span>
              <span>110° · ultra</span>
            </div>
          </HudCard>

          {/* Rotation */}
          <HudCard variant="glass">
            <div className="flex items-center justify-between mb-2">
              <span className="hud-eyebrow flex items-center gap-1">
                <RotateCw className="w-3 h-3" />
                Orientation
              </span>
              <span className="hud-tabular text-[13px] font-semibold" style={{ color: 'var(--c-text)' }}>
                {angleDeg}°
              </span>
            </div>
            <Slider
              min={0} max={359} step={1}
              value={[angleDeg]}
              onValueChange={(v) => setAngleDeg(v[0])}
              data-testid="fps-angle-slider"
            />
            <p className="mt-1 text-[10px]" style={{ color: 'var(--c-text-faint)' }}>
              0° = est, 90° = sud, 180° = ouest, 270° = nord
            </p>
          </HudCard>

          {/* Calibration */}
          <HudCard variant="glass">
            <div className="flex items-center justify-between mb-2">
              <span className="hud-eyebrow flex items-center gap-1">
                <Ruler className="w-3 h-3" />
                Calibration m/pixel
              </span>
              {scaleMPerPx !== null && (
                <span className="hud-tabular text-[11px] font-semibold" style={{ color: 'var(--c-gold)' }}>
                  1px = {scaleMPerPx.toFixed(4)}m
                </span>
              )}
            </div>
            {calibMode === 'idle' && scaleMPerPx === null && (
              <button
                type="button"
                onClick={() => setCalibMode('pick1')}
                className="hud-btn hud-btn-ghost hud-btn-sm w-full"
                data-testid="fps-calib-start"
              >
                <Crosshair className="w-3 h-3" />
                Calibrer (2 clics + distance)
              </button>
            )}
            {(calibMode === 'pick1' || calibMode === 'pick2') && (
              <div className="text-[11px] flex items-start gap-1.5" style={{ color: '#5EEAD4' }}>
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                <span>
                  {calibMode === 'pick1' ? 'Clique le point A sur le plan.' : 'Clique le point B (distance connue).'}
                </span>
              </div>
            )}
            {calibMode === 'done' && scaleMPerPx === null && (
              <div className="space-y-2">
                <input
                  type="number"
                  min="0.1" step="0.1"
                  value={calibMeters}
                  onChange={(e) => setCalibMeters(e.target.value)}
                  placeholder="Distance réelle en mètres (ex: 4.20)"
                  className="w-full px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-[12px] text-white outline-none focus:border-[var(--c-gold)]"
                  data-testid="fps-calib-meters"
                />
                <div className="flex gap-1.5">
                  <button onClick={computeCalibration} className="hud-btn hud-btn-primary hud-btn-sm flex-1" data-testid="fps-calib-confirm">
                    Calculer
                  </button>
                  <button onClick={resetCalibration} className="hud-btn hud-btn-ghost hud-btn-sm">
                    Reset
                  </button>
                </div>
              </div>
            )}
            {scaleMPerPx !== null && (
              <button onClick={resetCalibration} className="hud-btn hud-btn-ghost hud-btn-sm w-full mt-1" data-testid="fps-calib-reset">
                Recalibrer
              </button>
            )}
          </HudCard>

          {/* Readout */}
          <HudCard variant="glass">
            <span className="hud-eyebrow mb-2 block">État caméra</span>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <p style={{ color: 'var(--c-text-faint)' }}>Position X</p>
                <p className="hud-tabular font-semibold mt-0.5" style={{ color: 'var(--c-text)' }}>
                  {cameraX.toFixed(3)}
                </p>
              </div>
              <div>
                <p style={{ color: 'var(--c-text-faint)' }}>Position Y</p>
                <p className="hud-tabular font-semibold mt-0.5" style={{ color: 'var(--c-text)' }}>
                  {cameraY.toFixed(3)}
                </p>
              </div>
              <div>
                <p style={{ color: 'var(--c-text-faint)' }}>Hauteur Z</p>
                <p className="hud-tabular font-semibold mt-0.5" style={{ color: 'var(--c-gold)' }}>
                  {HUMAN_EYE_HEIGHT_M}m
                </p>
              </div>
              <div>
                <p style={{ color: 'var(--c-text-faint)' }}>Angle</p>
                <p className="hud-tabular font-semibold mt-0.5" style={{ color: 'var(--c-text)' }}>
                  {angleDeg}°
                </p>
              </div>
            </div>
          </HudCard>

          {/* CTA */}
          <Button
            onClick={handleConfirm}
            disabled={!selectedLot}
            className="w-full"
            data-testid="fps-generate-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Générer la vue FPS
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FpsCameraPlanner;
